import React, { HTMLAttributes } from 'react';
import {
  polymorphicForwardRef,
  type GDSCustomizableComponent,
  type GDSHorizontalAlignmentValues,
  type GDSVerticalAlignmentValues,
  type PolymorphicComponentPropWithRef,
  type PolymorphicRef,
} from '../../../types';
import { styled } from '../../../theme';

type GDSBaseWidth = 'hugContents' | (string & {});

const BaseStackItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  variants: {
    horizontalAlignment: {
      fillContainer: {
        alignItems: 'stretch',
      },
      start: {
        alignItems: 'flex-start',
      },
      center: {
        alignItems: 'center',
      },
      end: {
        alignItems: 'flex-end',
      },
    },
    verticalAlignment: {
      fillContainer: {
        justifyContent: 'stretch',
        '& > *': {
          flexGrow: 1,
        },
      },
      top: {
        justifyContent: 'flex-start',
      },
      center: {
        justifyContent: 'center',
      },
      bottom: {
        justifyContent: 'flex-end',
      },
    },
  },
});

const setFlexBasis = (baseWidth: GDSBaseWidth) =>
  baseWidth === 'hugContents' ? 'fit-content' : baseWidth;

export type GDSStackItemProps<C extends React.ElementType> =
  PolymorphicComponentPropWithRef<
    C,
    {
      as?: React.ElementType;
      children?: React.ReactNode;
      horizontalAlignment?: 'fillContainer' | GDSHorizontalAlignmentValues;
      verticalAlignment?: 'fillContainer' | GDSVerticalAlignmentValues;
      baseWidth?: GDSBaseWidth;
      growFactor?: number;
      shrinkFactor?: number;
      itemOrder?: number;
    }
  > &
    GDSCustomizableComponent &
    HTMLAttributes<HTMLElement>;

type GDSStackItemComponent = <C extends React.ElementType = 'div'>(
  props: GDSStackItemProps<C>,
) => React.ReactElement | null;

export const StackItem: GDSStackItemComponent = polymorphicForwardRef(
  <C extends React.ElementType = 'div'>(
    {
      horizontalAlignment = 'fillContainer',
      verticalAlignment = 'fillContainer',
      growFactor,
      shrinkFactor,
      baseWidth,
      itemOrder,
      children,
      className,
      css,
      ...props
    }: GDSStackItemProps<C>,
    ref: PolymorphicRef<C>,
  ) => (
    <BaseStackItem
      horizontalAlignment={horizontalAlignment}
      verticalAlignment={verticalAlignment}
      ref={ref}
      className={['GDS-stack-item', className].join(' ')}
      css={{
        ...css,
        ...{ flexGrow: growFactor, flexShrink: shrinkFactor },
        ...(baseWidth && { flexBasis: setFlexBasis(baseWidth) }),
        ...(itemOrder && { order: itemOrder }),
      }}
      {...props}
    >
      {children}
    </BaseStackItem>
  ),
);
